import { useMemo } from 'react';
import { Box, Card, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import { StLedgerEntry } from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { StEmpty, StIdCell, StNameIdCell, stFormatDateTime, stText } from '../StListUi';
import { socialTradingLedgerEntriesByPacketPath } from '../redpacketNav';
import './init';
import '../redpacket-platform-ledger/init';

function parseUrlFilter(search: string): Record<string, string> {
  try {
    const raw = new URLSearchParams(search).get('filter');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, string> = {};
    (['packetId', 'userId', 'roomId', 'roomName', 'nickname'] as const).forEach((key) => {
      const v = parsed[key];
      if (typeof v === 'string' && v.trim()) out[key] = v.trim();
    });
    return out;
  } catch {
    return {};
  }
}

function directionLabel(t: (key: string) => string, direction?: string | null): string {
  if (!direction) return '—';
  const key = `module.socialTrading.ledgerDirection.${direction}`;
  const label = t(key);
  return label === key ? direction : label;
}

const UserFilter = (props: any) => (
  <Filter {...(props || {})}>
    <TextInput source="packetId" label="packetId" alwaysOn resettable />
    <TextInput source="roomId" label="roomId" alwaysOn resettable />
    <TextInput source="roomName" label="roomName" alwaysOn resettable />
    <TextInput source="userId" label="userId" alwaysOn resettable />
    <TextInput source="nickname" label="nickname" alwaysOn resettable />
  </Filter>
);

const PlatformFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <SelectInput
        source="direction"
        label={t('module.socialTrading.colDirection')}
        choices={[
          { id: 'RESERVE', name: t('module.socialTrading.ledgerDirection.RESERVE') },
          { id: 'CLAIM_RELEASE', name: t('module.socialTrading.ledgerDirection.CLAIM_RELEASE') },
          { id: 'REFUND', name: t('module.socialTrading.ledgerDirection.REFUND') },
          { id: 'EXPIRY_REFUND', name: t('module.socialTrading.ledgerDirection.EXPIRY_REFUND') },
          { id: 'BUDGET_ADJUST', name: t('module.socialTrading.ledgerDirection.BUDGET_ADJUST') },
          { id: 'USER_FUND', name: t('module.socialTrading.ledgerDirection.USER_FUND') },
        ]}
        alwaysOn
        resettable
      />
      <TextInput source="packetId" label="packetId" alwaysOn resettable />
      <TextInput source="roomId" label="roomId" alwaysOn resettable />
      <TextInput source="roomName" label="roomName" alwaysOn resettable />
    </Filter>
  );
};

function PacketIdField({ row }: { row: StLedgerEntry }) {
  if (!row.packetId) return <StEmpty />;
  return (
    <StIdCell value={row.packetId} to={socialTradingLedgerEntriesByPacketPath(row.packetId)} />
  );
}

export default function SocialTradingRedpacketLedgerEntriesList() {
  const t = useTranslate();
  const { search } = useLocation();
  const urlFilter = useMemo(() => parseUrlFilter(search), [search]);
  const platformDefaultFilter = useMemo(() => {
    const f: Record<string, string> = {};
    if (urlFilter.packetId) f.packetId = urlFilter.packetId;
    if (urlFilter.roomId) f.roomId = urlFilter.roomId;
    if (urlFilter.roomName) f.roomName = urlFilter.roomName;
    return f;
  }, [urlFilter]);

  return (
    <PermissionGuard resource="st-ledger-entries" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.redpacketLedgerEntries')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.redpacketLedgerEntries') },
          ]}
        />

        <Card sx={{ mt: 2, pb: 2 }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('module.socialTrading.userLedgerSection')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('module.socialTrading.userLedgerHint')}
            </Typography>
          </Box>
          <List
            key={`user-ledger-${search}`}
            resource={Resources.socialTradingRedpacketLedgerEntries}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<UserFilter />}
            filterDefaultValues={urlFilter}
            storeKey="st-user-ledger"
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyLedger')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colDirection')}
                  render={(row: StLedgerEntry) => stText(directionLabel(t, row.direction))}
                />
                <FunctionField
                  label="packetId"
                  render={(row: StLedgerEntry) => <PacketIdField row={row} />}
                />
                <FunctionField
                  label="room"
                  render={(row: StLedgerEntry) => (
                    <StNameIdCell name={row.roomName} id={row.roomId} />
                  )}
                />
                <FunctionField
                  label="user"
                  render={(row: StLedgerEntry) => {
                    if (!row.userId && !row.nickname) return <StEmpty />;
                    return <StNameIdCell name={row.nickname} id={row.userId} />;
                  }}
                />
                <FunctionField label="amount" render={(row: StLedgerEntry) => stText(row.amount)} />
                <FunctionField
                  label="createdAt"
                  render={(row: StLedgerEntry) => stText(stFormatDateTime(row.createdAt))}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('module.socialTrading.platformLedgerSection')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('module.socialTrading.platformLedgerHint')}
            </Typography>
          </Box>
          <List
            key={`platform-ledger-${JSON.stringify(platformDefaultFilter)}`}
            resource={Resources.socialTradingRedpacketPlatformLedger}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<PlatformFilter />}
            filterDefaultValues={platformDefaultFilter}
            storeKey="st-platform-ledger"
            disableSyncWithLocation
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyLedger')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colDirection')}
                  render={(row: StLedgerEntry) => stText(directionLabel(t, row.direction))}
                />
                <FunctionField
                  label="packetId"
                  render={(row: StLedgerEntry) => <PacketIdField row={row} />}
                />
                <FunctionField
                  label="room"
                  render={(row: StLedgerEntry) => (
                    <StNameIdCell name={row.roomName} id={row.roomId} />
                  )}
                />
                <FunctionField label="amount" render={(row: StLedgerEntry) => stText(row.amount)} />
                <FunctionField
                  label={t('module.socialTrading.colBalance')}
                  render={(row: StLedgerEntry) => stText(row.balanceAfter)}
                />
                <FunctionField
                  label={t('module.socialTrading.colPoolType')}
                  render={(row: StLedgerEntry) => stText(row.poolType)}
                />
                <FunctionField
                  label="createdAt"
                  render={(row: StLedgerEntry) => stText(stFormatDateTime(row.createdAt))}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
